import {useQueryClient} from "@tanstack/react-query";
import {Alert, Checkbox, Button, Group, Table, Title} from "@mantine/core";
import { ArrowClockwise as IconRefresh } from "@phosphor-icons/react";
import React, {useCallback, useEffect, useState} from "react";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import Page from "../../components/Page/Page.tsx";
import api, {useSWCLocation, useUser} from "../../utils/api";


const Permission = React.memo(function PermissionRaw({name, description, status, update, required=false}) {
    return (<Table.Tr>
        <Table.Td><Checkbox id={name} checked={status.includes(name)} onChange={e => update(name, e.currentTarget.checked)} disabled={required} label={name} /></Table.Td>
        <Table.Td>{description}</Table.Td>
    </Table.Tr>);
});

function SWCPermissions() {
    const {user} = useUser();
    const [status, setStatus] = useState([]);
    const [env, setEnv] = useState(null);
    const [isMissingCharacterRead, setIsMissingCharacterRead] = useState(false);

    const loadData = useCallback(() => {
        api.get('/envConfig').then(res => {
            setEnv(res.data);
        });
        api.get('/swc/permissions').then(res => {
            if(!res.data.includes('character_read')) {
                setIsMissingCharacterRead(true);
                res.data.push('character_read');
            }
            setStatus(res.data);
        })
    }, []);

    const update = useCallback((name, checked) => {
        if (checked) {
            setStatus(prevState => ([...prevState, name]));
        } else {
            setStatus(prevState => prevState.filter(el => el !== name));
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const grant = () => {
        if (env !== null) {
            const scopes = status.join(' ');
            window.open(`https://www.swcombine.com/ws/oauth2/auth/?response_type=code&client_id=${env.client_id}&redirect_uri=${encodeURIComponent(env.redirect_uri)}&scope=${encodeURIComponent(scopes)}&access_type=offline&renew_previously_granted=no&state=grant`, '_self');
        }
    }
    const verifyScopes = () => {
        api.get('/swc/permissions/verify').then(res => {
            alert('Permissions verified');
            setStatus(res.data);
        });
    }


    return (
        <Page>
            <title>SWC API Permissions</title>
            <Title order={2}>SWC API Permissions</Title>

            {isMissingCharacterRead ? (
                <Alert color="red" style={{marginBottom: '1rem'}}>You are missing the 'character_read' permission. This is required for authentication. Please grant it in the SWC API settings.</Alert>
            ) : null}

            <Table>
                <Permission name="character_read" description="Read basic character info, including ID, name, image and faction. Required for authentication." status={status} update={update} required={true}/>
                <Permission name="character_location" description="Read character position in the galaxy. This is used to help calculating distances and suggesting positions. The data is not stored." status={status} update={update}/>

                <Table.Tr>
                    <Table.Th colSpan={2}>
                        <Title order={4}>Production Module - Personal</Title>
                    </Table.Th>
                </Table.Tr>
                <Permission name="personal_inv_stations_read" description="Read your personal station inventory data. Required for production module station tracking." status={status} update={update}/>
                <Permission name="personal_inv_facilities_read" description="Read your personal facility inventory data. Required for production module facility tracking." status={status} update={update}/>
                <Permission name="personal_inv_materials_read" description="Read your personal materials inventory data. Required for production module facility tracking." status={status} update={update}/>

                <Table.Tr>
                    <Table.Th colSpan={2}>
                        <Title order={4}>Production Module - Faction</Title>
                    </Table.Th>
                </Table.Tr>
                <Permission name="faction_inv_stations_read" description="Read your personal station inventory data. Required for production module station tracking." status={status} update={update}/>
                <Permission name="faction_inv_facilities_read" description="Read your personal facility inventory data. Required for production module facility tracking." status={status} update={update}/>
                <Permission name="faction_inv_materials_read" description="Read your personal materials inventory data. Required for production module facility tracking." status={status} update={update}/>

                {user.isAdmin ? (
                    <>
                        <Table.Tr>
                            <Table.Th colSpan={2}>
                                <Title order={4}>Admin</Title>
                            </Table.Th>
                        </Table.Tr>

                        <Permission name="character_credits" description="Credit Log" status={status} update={update}/>
                    </>
                ) : null}
            </Table>
            <br/>
            {env !== null ? (<Button onClick={grant} style={{marginRight: 10}}>Grant permissions</Button>) : null}
            <Button onClick={verifyScopes}>Verify permissions</Button>

            {import.meta.env.DEV ? (
                <Button onClick={() => api.get('/user/location')}>Send test request</Button>
            ) : null}

            {status.includes('character_location') && <LocationDisplay />}
        </Page>
    )


}


const LocationDisplay = () => {
    const {location} = useSWCLocation();
    const queryClient = useQueryClient();
    return (
        <div>
            <hr />
            <Title order={2}>Location</Title>
            {location ? <LocationString location={location} /> : <i>Not available. This is usually because you are in hyper.</i>}<br/>
            <Button onClick={() => {
                queryClient.invalidateQueries({queryKey: ['user-location']})
            }} leftSection={<IconRefresh size={16} />}>Refresh Location</Button>
        </div>
    )
}

const LocationString = ({location}) => {
    return <div>
        Sector: {location.sector.value}<br />
        System: {location.system.value}<br />
        {location.planet.value ? <span>Planet: {location.planet.value}</span> : <span>Space: ({location.coordinates.system.attributes.x},{location.coordinates.system.attributes.y})</span>}
    </div>
}


export default withRouteErrorBoundary(SWCPermissions);
